import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from './stores/authStore'
import { initCrazyGames } from './services/crazygames'
import { bootstrapCrazyGamesAuth } from './services/crazygamesAuth'
import { isCrazyGames } from './services/portal'
import Layout from './components/Layout'
import Home from './pages/Home'
import Game from './pages/Game'
import Missions from './pages/Missions'
import Leaderboard from './pages/Leaderboard'
import Profile from './pages/Profile'
import Admin from './pages/Admin'
import Store from './pages/Store'
import Notifications from './pages/Notifications'
import CooldownTargets from './pages/CooldownTargets'
import HackedNodes from './pages/HackedNodes'
import Banned from './pages/Banned'
import './styles/App.css'

const PUBLIC_PATHS = ['/', '/banned']

function App() {
  const { checkAuth, banInfo, isAuthenticated, authChecked } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  // On CrazyGames, authentication is driven by the CrazyGames account/guest flow
  // (external logins aren't allowed there); everywhere else the normal session check
  // runs. itch.io / direct are unaffected.
  useEffect(() => {
    if (isCrazyGames) bootstrapCrazyGamesAuth()
    else checkAuth()
  }, [checkAuth])

  // Initialise the CrazyGames SDK (ads + analytics). No-op unless the SPA is running
  // inside the CrazyGames portal, so itch.io / direct logins are unaffected.
  useEffect(() => {
    initCrazyGames()
  }, [])

  // Poll the auth check so a ban applied mid-session (by an admin, or by the
  // multi-account guard) kicks the player out promptly — without a manual F5.
  // checkAuth() surfaces the ban (403 → banInfo) and clears the token; the effect
  // above then redirects to /banned.
  useEffect(() => {
    if (!isAuthenticated) return
    const id = setInterval(() => { checkAuth() }, 15000)
    return () => clearInterval(id)
  }, [isAuthenticated, checkAuth])

  // A banned player (at login or kicked mid-session) is forced onto /banned and
  // cannot reach any game page until the ban is cleared.
  useEffect(() => {
    if (banInfo && location.pathname !== '/banned') {
      navigate('/banned', { replace: true })
    }
  }, [banInfo, location.pathname, navigate])

  // Central route guard: once the initial auth check has resolved, send
  // unauthenticated users on a protected route back to the login/home screen.
  // Gating on authChecked (not just isAuthenticated) prevents a fresh reload from
  // redirecting before the session cookie has been validated. This also handles
  // logout: clearing isAuthenticated bounces the user out of any protected page.
  useEffect(() => {
    if (!authChecked || banInfo) return
    if (!isAuthenticated && !PUBLIC_PATHS.includes(location.pathname)) {
      navigate('/', { replace: true })
    }
  }, [authChecked, isAuthenticated, banInfo, location.pathname, navigate])

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/game" element={<Game />} />
        <Route path="/missions" element={<Missions />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/store" element={<Store />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/cooldowns" element={<CooldownTargets />} />
        <Route path="/hacked-nodes" element={<HackedNodes />} />
        <Route path="/banned" element={<Banned />} />
      </Routes>
    </Layout>
  )
}

export default App
